import React, { useContext } from 'react'
import ReactDOM from 'react-dom'
import { useQuery } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import { GET_DUE_ITEMS_QUERY } from '../query/gqlQueries.js'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'

// Turn a due item into the flashcard shape the existing practice pipeline uses.
const toCard = (it) => ({
  sourceId: it.id,
  source: it.text,
  sourceLanguage: it.lang,
  sourceImageUrl: null,
  sourceImageDescription: null,
  translations: it.translationText
    ? [{ targetId: it.translationId, target: it.translationText, targetLanguage: it.translationLang || 'eng' }]
    : [],
})

export default function ReviewDueDialog({ onStartReview }) {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)
  const isOpen = dialogStates['dialogReviewDue']

  const { data, loading, error } = useQuery(GET_DUE_ITEMS_QUERY, {
    variables: { username, limit: 40 },
    skip: !isOpen || !username,
    fetchPolicy: 'network-only',
  })

  if (!isOpen) return null
  const items = data?.getDueItems || []
  const close = () => closeDialog('dialogReviewDue')

  const start = () => {
    if (items.length === 0) return
    onStartReview(items.map(toCard))
    close()
  }

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={close}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
        <button className={styles.closeBtn} onClick={close}><CloseIcon /></button>

        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Spaced Repetition</div>
            <h2 className={styles.title}>Review due</h2>
          </div>
        </div>

        <div className={styles.container}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: '#8a8a95' }}>Checking what's due…</div>
          ) : error ? (
            <div style={{ color: '#f87171' }}>{error.message}</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
              <div style={{ fontSize: '2.2rem' }}>✅</div>
              <h3 style={{ margin: '0.5rem 0' }}>You're all caught up</h3>
              <p style={{ color: '#8a8a95', margin: 0 }}>Nothing is due for review right now. New practice will schedule items back here automatically.</p>
            </div>
          ) : (
            <>
              <p style={{ color: '#e8e8ec', margin: '0 0 0.8rem' }}>
                <strong style={{ color: '#6ee7b7', fontSize: '1.4rem' }}>{items.length}</strong> item{items.length === 1 ? '' : 's'} due — practise them before they fade.
              </p>
              <div style={{ maxHeight: 240, overflowY: 'auto', background: '#141418', border: '1px solid #2a2a33', borderRadius: 12, padding: '0.7rem 0.9rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {items.map((it) => {
                  const acc = it.timesPracticed ? Math.round((it.timesCorrect / it.timesPracticed) * 100) : null
                  return (
                    <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.8rem', fontSize: '0.9rem' }}>
                      <span><span style={{ color: '#6ee7b7', fontWeight: 600 }}>{it.text}</span>{it.translationText ? <span style={{ color: '#8a8a95' }}> — {it.translationText}</span> : null}</span>
                      {acc != null && <span style={{ color: acc >= 70 ? '#8a8a95' : '#fbbf24', whiteSpace: 'nowrap' }}>{acc}%</span>}
                    </div>
                  )
                })}
              </div>
              <button className={styles.input} style={{ cursor: 'pointer', width: '100%', padding: '0.75rem', background: '#34d399', color: '#06281d', fontWeight: 600, border: 'none', marginTop: '1rem' }} onClick={start}>
                ▶ Start review ({items.length})
              </button>
            </>
          )}
        </div>
      </div>
    </div>, document.body
  )
}
